import { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Truck, Pencil, MagnifyingGlass, Calculator } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_FORMULA = {
  base_price: 100,
  base_km: 7,
  extra_per_km: 5,
  max_price: 135,
  offer_enabled: false,
  offer_discount_pct: 0,
  offer_start_date: '',
  offer_end_date: '',
  offer_badge_text: '',
};

export const ShippingCosts = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [formula, setFormula] = useState(DEFAULT_FORMULA);
  const [editingFormula, setEditingFormula] = useState(false);
  const [formulaDraft, setFormulaDraft] = useState(DEFAULT_FORMULA);

  const fetchAll = async () => {
    try {
      const { data } = await axios.get(`${API}/config/shipping-formula`, { withCredentials: true });
      const merged = { ...DEFAULT_FORMULA, ...data };
      // Normalizar campos posiblemente null
      merged.offer_start_date = merged.offer_start_date || '';
      merged.offer_end_date = merged.offer_end_date || '';
      merged.offer_badge_text = merged.offer_badge_text || '';
      merged.offer_discount_pct = merged.offer_discount_pct ?? 0;
      merged.offer_enabled = !!merged.offer_enabled;
      setFormula(merged);
      setFormulaDraft(merged);
    } catch (e) {
      toast.error('Error al cargar costos de envío');
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const saveFormula = async () => {
    try {
      const payload = {
        base_price: parseFloat(formulaDraft.base_price) || 0,
        base_km: parseFloat(formulaDraft.base_km) || 0,
        extra_per_km: parseFloat(formulaDraft.extra_per_km) || 0,
        max_price: parseFloat(formulaDraft.max_price) || 0,
        offer_enabled: !!formulaDraft.offer_enabled,
        offer_discount_pct: Math.max(0, Math.min(100, parseFloat(formulaDraft.offer_discount_pct) || 0)),
        offer_start_date: formulaDraft.offer_start_date || null,
        offer_end_date: formulaDraft.offer_end_date || null,
        offer_badge_text: (formulaDraft.offer_badge_text || '').trim() || null,
      };
      // Validaciones ligeras cuando la oferta está activa
      if (payload.offer_enabled) {
        if (payload.offer_discount_pct <= 0) {
          toast.error('El descuento debe ser mayor a 0%');
          return;
        }
        if (payload.offer_start_date && payload.offer_end_date && payload.offer_start_date > payload.offer_end_date) {
          toast.error('La fecha de inicio no puede ser posterior a la fecha final');
          return;
        }
      }
      await axios.put(`${API}/config/shipping-formula`, payload, { withCredentials: true });
      toast.success('Configuración guardada');
      setEditingFormula(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <MainLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Truck size={32} weight="bold" className="text-blue-600" />
            <div>
              <h1 className="text-4xl font-bold tracking-tight">Costos de Envío</h1>
              <p className="text-muted-foreground">Fórmula por km y ofertas para el cotizador público (<code>/cotizador</code>)</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Fórmula */}
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2"><Calculator size={18} /> Fórmula por km + Oferta</h2>
              {isAdmin && !editingFormula && (
                <Button size="sm" variant="ghost" onClick={() => setEditingFormula(true)} data-testid="edit-formula-btn">
                  <Pencil size={14} />
                </Button>
              )}
            </div>
            {!editingFormula ? (
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">0 a {formula.base_km} km:</span><span className="font-semibold">${formula.base_price.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Por km extra:</span><span className="font-semibold">${formula.extra_per_km.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Máximo:</span><span className="font-semibold">${formula.max_price.toFixed(2)}</span></div>
                {/* Oferta activa */}
                <div className="mt-3 pt-3 border-t border-dashed">
                  {formula.offer_enabled && formula.offer_discount_pct > 0 ? (
                    <div className="space-y-1" data-testid="offer-summary">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">🎁 Oferta activa</span>
                        <span className="text-lg font-bold text-emerald-700">-{formula.offer_discount_pct}%</span>
                      </div>
                      {(formula.offer_start_date || formula.offer_end_date) && (
                        <p className="text-[11px] text-slate-600">
                          Vigencia: {formula.offer_start_date || '—'} → {formula.offer_end_date || '—'}
                        </p>
                      )}
                      {formula.offer_badge_text && (
                        <p className="text-[11px] italic text-slate-600">Texto badge: “{formula.offer_badge_text}”</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">Sin oferta configurada</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Precio base ($)</Label>
                  <Input type="number" step="0.01" value={formulaDraft.base_price} onChange={(e) => setFormulaDraft({ ...formulaDraft, base_price: e.target.value })} data-testid="formula-base-price" />
                </div>
                <div>
                  <Label className="text-xs">Hasta km</Label>
                  <Input type="number" step="0.1" value={formulaDraft.base_km} onChange={(e) => setFormulaDraft({ ...formulaDraft, base_km: e.target.value })} data-testid="formula-base-km" />
                </div>
                <div>
                  <Label className="text-xs">Por km extra ($)</Label>
                  <Input type="number" step="0.01" value={formulaDraft.extra_per_km} onChange={(e) => setFormulaDraft({ ...formulaDraft, extra_per_km: e.target.value })} data-testid="formula-extra-km" />
                </div>
                <div>
                  <Label className="text-xs">Máximo ($)</Label>
                  <Input type="number" step="0.01" value={formulaDraft.max_price} onChange={(e) => setFormulaDraft({ ...formulaDraft, max_price: e.target.value })} data-testid="formula-max" />
                </div>

                {/* Sección Oferta */}
                <div className="mt-3 pt-3 border-t border-dashed space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!formulaDraft.offer_enabled}
                      onChange={(e) => setFormulaDraft({ ...formulaDraft, offer_enabled: e.target.checked })}
                      className="w-4 h-4"
                      data-testid="offer-enabled"
                    />
                    <span className="text-sm font-semibold text-emerald-700">🎁 Activar oferta de envío</span>
                  </label>
                  {formulaDraft.offer_enabled && (
                    <div className="pl-6 space-y-2">
                      <div>
                        <Label className="text-xs">Descuento (%)</Label>
                        <Input
                          type="number" step="1" min="0" max="100"
                          value={formulaDraft.offer_discount_pct}
                          onChange={(e) => setFormulaDraft({ ...formulaDraft, offer_discount_pct: e.target.value })}
                          data-testid="offer-discount-pct"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Desde</Label>
                          <Input
                            type="date"
                            value={formulaDraft.offer_start_date || ''}
                            onChange={(e) => setFormulaDraft({ ...formulaDraft, offer_start_date: e.target.value })}
                            data-testid="offer-start-date"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Hasta</Label>
                          <Input
                            type="date"
                            value={formulaDraft.offer_end_date || ''}
                            onChange={(e) => setFormulaDraft({ ...formulaDraft, offer_end_date: e.target.value })}
                            data-testid="offer-end-date"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Texto del badge (público)</Label>
                        <Input
                          type="text"
                          maxLength={80}
                          placeholder='Ej: "Envío -20% hasta el 31 de dic"'
                          value={formulaDraft.offer_badge_text || ''}
                          onChange={(e) => setFormulaDraft({ ...formulaDraft, offer_badge_text: e.target.value })}
                          data-testid="offer-badge-text"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Se muestra al cliente en el cotizador público debajo del precio.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="ghost" onClick={() => { setEditingFormula(false); setFormulaDraft(formula); }}>Cancelar</Button>
                  <Button size="sm" onClick={saveFormula} data-testid="save-formula">Guardar</Button>
                </div>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">
              La oferta se aplica al precio final del envío en el cotizador público.
            </p>
          </div>

          {/* Ayuda: cómo probar el cotizador */}
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
            <h2 className="font-semibold mb-3 flex items-center gap-2 text-blue-900">
              <MagnifyingGlass size={18} /> ¿Cómo probar el cotizador?
            </h2>
            <ol className="text-sm text-slate-700 space-y-2 list-decimal pl-5">
              <li>Guarda la fórmula (y la oferta, si aplica) con el botón <b>Guardar</b>.</li>
              <li>Abre el cotizador público en <a href="/cotizador" target="_blank" rel="noreferrer" className="text-blue-700 underline font-medium">/cotizador</a>.</li>
              <li>Escribe una dirección y verifica que aparezcan:
                <ul className="list-disc pl-5 mt-1 text-xs text-slate-600 space-y-0.5">
                  <li>Precio normal (tachado si hay oferta)</li>
                  <li>Precio con descuento aplicado</li>
                  <li>Badge con el texto de vigencia</li>
                </ul>
              </li>
            </ol>
            <p className="text-[11px] text-muted-foreground mt-3">
              Las tarifas específicas por colonia se descontinuaron. Ahora el precio se calcula automáticamente por km + oferta.
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ShippingCosts;
